/* ═══════════════════════════════════════════════════════════════════════
   Constants — display labels, metric keys, card definitions, colors
   ═══════════════════════════════════════════════════════════════════════ */

/** Human-readable display names for XBRL metric keys */
export const LABEL: Record<string, string> = {
  // Income Statement
  revenue: "Revenue",
  cost_of_revenue: "Cost of Revenue",
  gross_profit: "Gross Profit",
  operating_income: "Operating Income",
  operating_expenses: "Operating Expenses",
  net_income: "Net Income",
  eps_basic: "EPS (Basic)",
  eps_diluted: "EPS (Diluted)",
  ebitda: "EBITDA",
  interest_expense: "Interest Expense",
  non_operating_income: "Non-operating Income",
  income_tax: "Income Tax",
  income_before_tax: "Income Before Tax",
  comprehensive_income: "Comprehensive Income",
  // Balance Sheet
  total_assets: "Total Assets",
  current_assets: "Current Assets",
  total_liabilities: "Total Liabilities",
  liabilities_and_equity: "Liabilities & Equity",
  current_liabilities: "Current Liabilities",
  noncurrent_liabilities: "Non-current Liabilities",
  stockholders_equity: "Equity",
  cash: "Cash & Equiv.",
  short_term_investments: "Short-term Investments",
  long_term_debt: "Long-term Debt",
  total_debt: "Total Debt",
  goodwill: "Goodwill",
  intangible_assets: "Intangible Assets",
  property_plant_equipment: "PP&E",
  retained_earnings: "Retained Earnings",
  shares_outstanding: "Shares Out.",
  shares_basic: "Shares (Basic)",
  inventory: "Inventory",
  other_current_assets: "Other Current Assets",
  operating_lease_assets: "Operating Lease Assets",
  long_term_investments: "Long-term Investments",
  equity_method_investments: "Equity Method Inv.",
  other_noncurrent_assets: "Other Non-current Assets",
  accounts_payable: "Accounts Payable",
  accrued_expenses: "Accrued Expenses",
  current_lease_liabilities: "Current Leases",
  current_debt: "Current Debt",
  current_deferred_revenue: "Unearned Revenue",
  noncurrent_lease_liabilities: "Non-current Leases",
  other_noncurrent_liabilities: "Other Long-term Liab.",
  noncurrent_deferred_revenue: "Non-current Def. Rev.",
  common_stock_value: "Common Stock",
  additional_paid_in_capital: "APIC",
  aoci: "AOCI",
  minority_interest: "Minority Interest",
  treasury_stock: "Treasury Stock",
  preferred_stock: "Preferred Stock",
  deferred_tax_assets: "Deferred Tax Assets",
  deferred_tax_liabilities: "Deferred Tax Liab.",
  selling_expense: "Selling & Marketing",
  current_tax_expense: "Current Tax Expense",
  deferred_tax_expense: "Deferred Tax Expense",
  prepayments: "Prepayments",
  other_current_liabilities: "Other Current Liab.",
  finance_income: "Finance Income",
  finance_costs: "Finance Costs",
  other_comprehensive_income: "Other Comp. Income",
  // Cash Flow
  operating_cash_flow: "Op. Cash Flow",
  investing_cash_flow: "Inv. Cash Flow",
  financing_cash_flow: "Fin. Cash Flow",
  capex: "Capital Expenditures",
  free_cash_flow: "Free Cash Flow",
  depreciation: "D&A",
  stock_based_comp: "SBC",
  dividends: "Dividends",
  buybacks: "Buybacks",
  net_change_in_cash: "Change in Cash",
  changes_in_working_capital: "NWC Changes",
  deferred_taxes_operating: "Deferred Income Taxes",
  other_non_cash_items: "Other Non-cash Adjust.",
  acquisitions: "Acquisitions (M&A)",
  purchases_of_investments: "Purchases of Investments",
  sales_of_investments: "Sales of Investments",
  other_investing_activities: "Other Investing",
  debt_issuance: "Debt Issuance",
  debt_repayment: "Debt Repayment",
  stock_issuance: "Stock Issuance",
  other_financing_activities: "Other Financing",
  // Expenses
  r_and_d: "R&D",
  sga: "SG&A",
  general_admin: "General & Admin",
  other_expenses: "Other Expenses",
  // Income Detail
  interest_income: "Interest Income",
  net_interest_income: "Net Interest Income",
  net_income_continuing: "NI from Continuing Ops",
  net_income_discontinued: "NI from Discontinued Ops",
  net_income_common: "Net Income to Common",
  ebit: "EBIT",
  shares_diluted: "Shares (Diluted)",
  // Missing IFRS & GAAP Items
  share_capital: "Share Capital",
  financial_assets_fvpl: "Financial Assets (FVPL)",
  financial_assets_fvoci: "Financial Assets (FVOCI)",
  financial_assets_afs: "Available-for-Sale Financial Assets",
  provisions_current: "Provisions (Current)",
  provisions_noncurrent: "Provisions (Non-current)",
  current_tax_liabilities: "Current Tax Liabilities",
  income_taxes_paid: "Income Taxes Paid",
  interest_received: "Interest Received",
  interest_paid_operating: "Interest Paid (Operating)",
  restructuring_charges: "Restructuring Charges",
  impairment_charges: "Impairment Charges",
  equity_earnings_affiliates: "Equity Earnings in Affiliates",
  restricted_cash: "Restricted Cash",
  income_taxes_receivable: "Income Taxes Receivable",
  income_taxes_payable: "Income Taxes Payable",
  pension_obligations: "Pension Obligations",
  change_in_accounts_receivable: "Change in A/R",
  change_in_inventory: "Change in Inventory",
  change_in_accounts_payable: "Change in A/P",
  effect_of_exchange_rate_on_cash: "FX Effect on Cash",
  // Cash Flow Detail
  // Per-share
  book_value_per_share: "Book Value/Share",
  dividends_per_share: "Dividends/Share",
  // DEI
  entity_common_stock_shares_outstanding: "Entity Shares Out.",
  entity_public_float: "Public Float",
  // Ratios & Valuation
  stock_price: "Stock Price",
  market_cap: "Market Cap",
  p_e: "P/E Ratio",
  p_s: "P/S Ratio",
  p_b: "P/B Ratio",
  p_fcf: "P/FCF Ratio",
  tev: "Total Enterprise Value",
  gross_margin: "Gross Margin",
  operating_margin: "Operating Margin",
  net_margin: "Net Margin",
  fcf_margin: "FCF Margin",
  roa: "Return on Assets (ROA)",
  roe: "Return on Equity (ROE)",
  current_ratio: "Current Ratio",
  debt_to_equity: "Debt to Equity",
  asset_turnover: "Asset Turnover",
  price_to_gross_profit: "P/Gross Profit",
};

/** All available metric keys (derived from LABEL) */
export const ALL_METRICS = Object.keys(LABEL);

/** Structured categories for the metric selector UI */
export interface MetricCategory {
  title: string;
  keys: string[];
}

export const METRIC_CATEGORIES: MetricCategory[] = [
  {
    title: "Income Statement",
    keys: [
      "revenue",
      "cost_of_revenue",
      "gross_profit",
      "operating_expenses",
      "r_and_d",
      "sga",
      "selling_expense",
      "general_admin",
      "other_expenses",
      "operating_income",
      "interest_income",
      "interest_expense",
      "net_interest_income",
      "non_operating_income",
      "income_before_tax",
      "income_tax",
      "net_income",
      "net_income_continuing",
      "net_income_discontinued",
      "net_income_common",
      "ebitda",
      "ebit",
      "comprehensive_income",
      "other_comprehensive_income",
      "eps_basic",
      "eps_diluted",
    ],
  },
  {
    title: "Balance Sheet",
    keys: [
      "cash",
      "short_term_investments",
      "accounts_receivable",
      "inventory",
      "other_current_assets",
      "current_assets",
      "property_plant_equipment",
      "operating_lease_assets",
      "deferred_tax_assets",
      "goodwill",
      "intangible_assets",
      "long_term_investments",
      "equity_method_investments",
      "other_noncurrent_assets",
      "total_assets",
      "accounts_payable",
      "accrued_expenses",
      "current_debt",
      "current_lease_liabilities",
      "current_deferred_revenue",
      "current_liabilities",
      "long_term_debt",
      "noncurrent_lease_liabilities",
      "noncurrent_deferred_revenue",
      "deferred_tax_liabilities",
      "other_noncurrent_liabilities",
      "noncurrent_liabilities",
      "total_liabilities",
      "common_stock_value",
      "additional_paid_in_capital",
      "aoci",
      "retained_earnings",
      "treasury_stock",
      "minority_interest",
      "stockholders_equity",
      "liabilities_and_equity",
    ],
  },
  {
    title: "Cash Flow Statement",
    keys: [
      "net_income",
      "depreciation",
      "stock_based_comp",
      "deferred_taxes_operating",
      "other_non_cash_items",
      "changes_in_working_capital",
      "operating_cash_flow",
      "capex",
      "acquisitions",
      "purchases_of_investments",
      "sales_of_investments",
      "other_investing_activities",
      "investing_cash_flow",
      "debt_issuance",
      "debt_repayment",
      "stock_issuance",
      "dividends",
      "buybacks",
      "other_financing_activities",
      "financing_cash_flow",
      "net_change_in_cash",
      "free_cash_flow",
    ],
  },
  {
    title: "Per Share & Entity",
    keys: [
      "book_value_per_share",
      "dividends_per_share",
      "shares_outstanding",
      "shares_basic",
      "shares_diluted",
      "entity_common_stock_shares_outstanding",
      "entity_public_float",
    ],
  },
  {
    title: "Operating Metrics",
    keys: [
      "daus",
      "maus",
      "arpau",
      "subscribers",
    ],
  },
  {
    title: "Ratios & Valuation",
    keys: [
      "stock_price",
      "market_cap",
      "tev",
      "p_e",
      "p_s",
      "p_b",
      "p_fcf",
      "price_to_gross_profit",
      "gross_margin",
      "operating_margin",
      "net_margin",
      "fcf_margin",
      "roa",
      "roe",
      "current_ratio",
      "debt_to_equity",
      "asset_turnover",
    ],
  },
];

export const DEFAULT_CHART_TEMPLATES: { title: string; keys: string[] }[] = [];

/** Overview panel sparkline card definitions */
export interface CardDef {
  key: string;
  icon: string;
  colorClass: string;
  /** Tailwind bg class for the active bar */
  bgActive: string;
  /** Tailwind color for change badge and hover accents */
  accentColor: string;
}

export const CARD_DEFS: CardDef[] = [
  {
    key: "revenue",
    icon: "bar_chart",
    colorClass: "primary",
    bgActive: "bg-primary",
    accentColor: "primary",
  },
  {
    key: "gross_profit",
    icon: "bar_chart",
    colorClass: "accent-purple",
    bgActive: "bg-accent-purple",
    accentColor: "accent-purple",
  },
  {
    key: "net_income",
    icon: "bar_chart",
    colorClass: "accent-green",
    bgActive: "bg-accent-green",
    accentColor: "accent-green",
  },
];

/** Valid panel names for the ticker page */
export const VALID_PANELS = [
  "overview",
  "financials",
  "compare",
  "filings",
  "ownership",
] as const;

export type PanelName = (typeof VALID_PANELS)[number];

/* ═══════════════════════════════════════════════════════════════════════
   Statement row definitions — used by <StatementsPanel /> table
   ═══════════════════════════════════════════════════════════════════════ */

export interface StatementRow {
  key: string;
  label: string;
  bold?: boolean;
  /** Section start: thick border above for visual chunking */
  sectionStart?: boolean;
  /** Collapsible child rows */
  children?: StatementRow[];
  /** Instructs how to compute this row from its children if a native value isn't filed. */
  compute?: "sum" | "net";
  /** Identifies rows that are purely visual structural groupings, not data points. */
  isGroupHeader?: boolean;
}

/** Flatten hierarchical rows into a flat array with computed depth. */
export function flattenRows(
  rows: StatementRow[],
  depth = 0,
): { row: StatementRow; depth: number; parentKey?: string }[] {
  const out: { row: StatementRow; depth: number; parentKey?: string }[] = [];
  for (const r of rows) {
    out.push({ row: r, depth });
    if (r.children) {
      for (const child of r.children) {
        out.push({ row: child, depth: depth + 1, parentKey: r.key });
      }
    }
  }
  return out;
}

/** Collect all metric keys from a rows tree (including children). */
export function allRowKeys(rows: StatementRow[]): string[] {
  const keys: string[] = [];
  for (const r of rows) {
    if (!r.isGroupHeader) keys.push(r.key);
    if (r.children) {
      keys.push(...allRowKeys(r.children));
    }
  }
  return keys;
}

/** Collect only top-level metric keys (excludes children and group headers). */
export function topLevelRowKeys(rows: StatementRow[]): string[] {
  const keys: string[] = [];
  for (const r of rows) {
    if (!r.isGroupHeader && !r.children) {
      keys.push(r.key);
    } else if (!r.isGroupHeader && r.children) {
      // Include parent rows with children (they show aggregated values)
      keys.push(r.key);
    }
  }
  return keys;
}

export type StatementType = "income" | "balance" | "cashflow" | "supplemental" | "ratios" | "custom";

export const US_GAAP_STATEMENT_DEFS: Record<
  StatementType,
  { title: string; primaryMetric: string; rows: StatementRow[] }
> = {
  income: {
    title: "Income Statement",
    primaryMetric: "revenue",
    rows: [
      // Section 1: Revenue to Gross Profit
      { key: "revenue", label: "Revenue" },
      { key: "cost_of_revenue", label: "Cost of Revenue" },
      { key: "gross_profit", label: "Gross Profit" },

      // Section 2: Operating Expenses
      { key: "section_opex", label: "Operating Expenses", isGroupHeader: true, sectionStart: true },
      { key: "r_and_d", label: "Research & Development" },
      { key: "sga", label: "SG&A", compute: "sum", children: [
        { key: "general_admin", label: "General & Admin" },
        { key: "selling_expense", label: "Selling & Marketing" },
      ]},
      { key: "restructuring_charges", label: "Restructuring Charges" },
      { key: "impairment_charges", label: "Impairment Charges" },
      { key: "other_expenses", label: "Other Expenses" },
      { key: "operating_expenses", label: "Operating Expenses", compute: "sum", children: [
        { key: "sga", label: "SG&A" },
        { key: "selling_expense", label: "Selling & Marketing" },
        { key: "general_admin", label: "General & Admin" },
        { key: "r_and_d", label: "R&D" },
        { key: "restructuring_charges", label: "Restructuring Charges" },
        { key: "impairment_charges", label: "Impairment Charges" },
        { key: "other_expenses", label: "Other Operating Expenses" },
      ]},
      { key: "operating_income", label: "Operating Income" },

      // Section 3: Other Income/Expense
      { key: "section_other", label: "Other Income/Expense", isGroupHeader: true, sectionStart: true },
      { key: "interest_income", label: "Interest Income" },
      { key: "interest_expense", label: "Interest Expense" },
      { key: "net_interest_income", label: "Net Interest Income" },
      { key: "finance_income", label: "Finance Income" },
      { key: "finance_costs", label: "Finance Costs" },
      { key: "equity_earnings_affiliates", label: "Equity Earnings in Affiliates" },
      { key: "non_operating_income", label: "Non-operating Income" },
      { key: "other_income", label: "Other Income" },
      { key: "income_before_tax", label: "Pre-tax Income" },

      // Section 4: Taxes & Net Income
      { key: "section_net", label: "Net Income", isGroupHeader: true, sectionStart: true },
      { key: "income_tax", label: "Income Tax Expense", compute: "sum", children: [
        { key: "current_tax_expense", label: "Current Tax Expense" },
        { key: "deferred_tax_expense", label: "Deferred Tax Expense" }
      ]},
      { key: "net_income_continuing", label: "Net Income (Continuing Ops)" },
      { key: "net_income_discontinued", label: "Net Income (Discontinued Ops)" },
      { key: "net_income", label: "Net Income" },
      { key: "net_income_common", label: "Net Income to Common" },
      { key: "other_comprehensive_income", label: "Other Comprehensive Income" },
      { key: "comprehensive_income", label: "Comprehensive Income" },

      // Section 5: Per Share
      { key: "eps_basic", label: "EPS (Basic)", sectionStart: true },
      { key: "eps_diluted", label: "EPS (Diluted)" },
      { key: "shares_basic", label: "Shares (Basic)" },
      { key: "shares_diluted", label: "Shares (Diluted)" },
      { key: "book_value_per_share", label: "Book Value/Share" },
      { key: "dividends_per_share", label: "Dividends/Share" },

      // Section 6: Supplemental
      { key: "ebitda", label: "EBITDA", sectionStart: true },
      { key: "depreciation", label: "Depreciation & Amort." },
      { key: "ebit", label: "EBIT" },
    ],
  },
  balance: {
    title: "Balance Sheet",
    primaryMetric: "total_assets",
    rows: [
      // Section 1: Assets
      { key: "section_assets", label: "Assets", isGroupHeader: true, sectionStart: true },
      { key: "current_assets", label: "Current Assets", children: [
        { key: "cash", label: "Cash & Equivalents" },
        { key: "restricted_cash", label: "Restricted Cash" },
        { key: "short_term_investments", label: "Short-term Investments" },
        { key: "financial_assets_fvpl", label: "Financial Assets (FVPL)" },
        { key: "accounts_receivable", label: "Accounts Receivable" },
        { key: "income_taxes_receivable", label: "Income Taxes Receivable" },
        { key: "inventory", label: "Inventory" },
        { key: "prepayments", label: "Prepayments" },
        { key: "other_current_assets", label: "Other Current Assets" },
      ]},
      { key: "noncurrent_assets", label: "Non-current Assets", children: [
        { key: "property_plant_equipment", label: "PP&E" },
        { key: "operating_lease_assets", label: "Right-of-Use Assets" },
        { key: "deferred_tax_assets", label: "Deferred Tax Assets" },
        { key: "goodwill", label: "Goodwill" },
        { key: "intangible_assets", label: "Intangible Assets" },
        { key: "long_term_investments", label: "Long-term Investments" },
        { key: "equity_method_investments", label: "Equity Method Investments" },
        { key: "financial_assets_fvoci", label: "Financial Assets (FVOCI)" },
        { key: "financial_assets_afs", label: "Available-for-Sale Financial Assets" },
        { key: "other_noncurrent_assets", label: "Other Non-current Assets" },
      ]},
      { key: "total_assets", label: "Total Assets" },

      // Section 2: Liabilities
      { key: "section_liabilities", label: "Liabilities", isGroupHeader: true, sectionStart: true },
      { key: "current_liabilities", label: "Current Liabilities", children: [
        { key: "accounts_payable", label: "Accounts Payable" },
        { key: "accrued_expenses", label: "Accrued Expenses" },
        { key: "income_taxes_payable", label: "Income Taxes Payable" },
        { key: "current_debt", label: "Current Debt" },
        { key: "current_lease_liabilities", label: "Current Leases" },
        { key: "provisions_current", label: "Provisions" },
        { key: "current_tax_liabilities", label: "Current Tax Liabilities" },
        { key: "current_deferred_revenue", label: "Unearned/Deferred Revenue" },
        { key: "other_current_liabilities", label: "Other Current Liabilities" },
      ]},
      { key: "noncurrent_liabilities", label: "Non-current Liabilities", children: [
        { key: "long_term_debt", label: "Long-term Debt" },
        { key: "noncurrent_lease_liabilities", label: "Non-current Leases" },
        { key: "pension_obligations", label: "Pension Obligations" },
        { key: "provisions_noncurrent", label: "Provisions (Non-current)" },
        { key: "noncurrent_deferred_revenue", label: "Non-current Deferred Revenue" },
        { key: "deferred_tax_liabilities", label: "Deferred Tax Liabilities" },
        { key: "other_noncurrent_liabilities", label: "Other Long-term Liabilities" },
      ]},
      { key: "total_liabilities", label: "Total Liabilities" },
      { key: "total_debt", label: "Total Debt" },

      // Section 3: Stockholders' Equity
      { key: "section_equity", label: "Stockholders' Equity", isGroupHeader: true, sectionStart: true },
      { key: "stockholders_equity", label: "Stockholders' Equity", children: [
        { key: "preferred_stock", label: "Preferred Stock" },
        { key: "common_stock_value", label: "Common Stock" },
        { key: "share_capital", label: "Share Capital" },
        { key: "additional_paid_in_capital", label: "Additional Paid-in Capital" },
        { key: "aoci", label: "Accumulated OCI" },
        { key: "retained_earnings", label: "Retained Earnings" },
        { key: "treasury_stock", label: "Treasury Stock" },
        { key: "minority_interest", label: "Minority Interest" },
      ]},
      { key: "liabilities_and_equity", label: "Liabilities & Equity", sectionStart: true },
      { key: "section_shares", label: "Shares", isGroupHeader: true, sectionStart: true },
      { key: "shares_outstanding", label: "Shares Outstanding" },
      { key: "shares_basic", label: "Shares (Basic)" },
      { key: "entity_common_stock_shares_outstanding", label: "Entity Shares Out." },
      { key: "entity_public_float", label: "Public Float" },
    ],
  },
  cashflow: {
    title: "Cash Flow Statement",
    primaryMetric: "operating_cash_flow",
    rows: [
      // Section 1: Operating
      { key: "operating_cash_flow", label: "Operating Activities", sectionStart: true, children: [
        { key: "net_income", label: "Net Income" },
        { key: "depreciation", label: "Depreciation & Amortization" },
        { key: "stock_based_comp", label: "Stock-Based Compensation" },
        { key: "finance_costs", label: "Finance Costs" },
        { key: "changes_in_working_capital", label: "Changes in Working Capital", children: [
          { key: "change_in_accounts_receivable", label: "Accounts Receivable" },
          { key: "change_in_inventory", label: "Inventory" },
          { key: "change_in_accounts_payable", label: "Accounts Payable" },
        ]},
        { key: "income_taxes_paid", label: "Income Taxes Paid" },
        { key: "interest_paid_operating", label: "Interest Paid" },
        { key: "interest_received", label: "Interest Received" },
        { key: "deferred_taxes_operating", label: "Deferred Income Taxes" },
        { key: "other_non_cash_items", label: "Other Non-cash Adjustments" },
      ]},

      // Section 2: Investing
      { key: "investing_cash_flow", label: "Investing Activities", sectionStart: true, children: [
        { key: "capex", label: "Capital Expenditures" },
        { key: "acquisitions", label: "Acquisitions (M&A)" },
        { key: "purchases_of_investments", label: "Purchases of Investments" },
        { key: "sales_of_investments", label: "Sales of Investments" },
        { key: "other_investing_activities", label: "Other Investing Activities" },
      ]},

      // Section 3: Financing
      { key: "financing_cash_flow", label: "Financing Activities", sectionStart: true, children: [
        { key: "debt_issuance", label: "Debt Issuance" },
        { key: "debt_repayment", label: "Debt Repayment" },
        { key: "stock_issuance", label: "Stock Issuance" },
        { key: "dividends", label: "Dividends Paid" },
        { key: "buybacks", label: "Share Buybacks" },
        { key: "other_financing_activities", label: "Other Financing Activities" },
      ]},

      // Section 4: Summary
      { key: "effect_of_exchange_rate_on_cash", label: "FX Effect on Cash" },
      { key: "net_change_in_cash", label: "Net Change in Cash", sectionStart: true },
      { key: "free_cash_flow", label: "Free Cash Flow" },
    ],
  },
  ratios: {
    title: "Ratios & Margins",
    primaryMetric: "market_cap",
    rows: [
      { key: "section_valuation", label: "Valuation", isGroupHeader: true },
      { key: "stock_price", label: "Stock Price" },
      { key: "shares_outstanding", label: "Total Shares Outstanding" },
      { key: "market_cap", label: "Market Cap" },
      { key: "tev", label: "Total Enterprise Value" },
      { key: "p_e", label: "P/E" },
      { key: "p_s", label: "P/S" },
      { key: "p_fcf", label: "P/FCF" },
      { key: "p_b", label: "P/B" },
      { key: "price_to_gross_profit", label: "P/Gross Profit" },

      { key: "section_margins", label: "Margins", isGroupHeader: true, sectionStart: true },
      { key: "gross_margin", label: "Gross Margin" },
      { key: "operating_margin", label: "Operating Margin" },
      { key: "net_margin", label: "Net Margin" },
      { key: "fcf_margin", label: "FCF Margin" },

      { key: "section_efficiency", label: "Capital Efficiency", isGroupHeader: true, sectionStart: true },
      { key: "roa", label: "ROA (Return on Assets)" },
      { key: "roe", label: "ROE (Return on Equity)" },
      { key: "asset_turnover", label: "Asset Turnover" },

      { key: "section_health", label: "Financial Health", isGroupHeader: true, sectionStart: true },
      { key: "current_ratio", label: "Current Ratio" },
      { key: "debt_to_equity", label: "Debt to Equity" },
    ],
  },
  supplemental: {
    title: "Supplemental Data",
    primaryMetric: "dividends",
    rows: []
  },
  custom: {
    title: "Custom Metrics",
    primaryMetric: "", // No default primary metric for custom
    rows: [], // Populated dynamically in UI
  },
};

export const STATEMENT_DEFS = US_GAAP_STATEMENT_DEFS;

export const IFRS_STATEMENT_DEFS: Record<
  StatementType,
  { title: string; primaryMetric: string; rows: StatementRow[] }
> = {
  income: {
    title: "Income Statement",
    primaryMetric: "revenue",
    rows: [
      { key: "revenue", label: "Revenue" },
      { key: "cost_of_revenue", label: "Cost of Sales" },
      { key: "gross_profit", label: "Gross Profit" },
      { key: "section_opex", label: "Operating Expenses", isGroupHeader: true, sectionStart: true },
      { key: "r_and_d", label: "Research & Development" },
      { key: "sga", label: "Administrative & Distribution Expenses", compute: "sum", children: [
        { key: "general_admin", label: "Administrative Expenses" },
        { key: "selling_expense", label: "Distribution Costs" },
      ]},
      { key: "operating_expenses", label: "Operating Expenses", compute: "sum", children: [
        { key: "sga", label: "Administrative & Distribution Expenses" },
        { key: "r_and_d", label: "R&D" },
        { key: "other_expenses", label: "Other Operating Expenses" },
      ]},
      { key: "operating_income", label: "Operating Profit" },
      { key: "section_finance", label: "Finance Income / Costs", isGroupHeader: true, sectionStart: true },
      { key: "finance_income", label: "Finance Income" },
      { key: "finance_costs", label: "Finance Costs" },
      { key: "equity_earnings_affiliates", label: "Share of Profit of Associates" },
      { key: "income_before_tax", label: "Profit Before Tax" },
      { key: "income_tax", label: "Income Tax Expense" },
      { key: "net_income", label: "Profit for the Period" },
      { key: "other_comprehensive_income", label: "Other Comprehensive Income" },
      { key: "comprehensive_income", label: "Total Comprehensive Income" },
      { key: "eps_basic", label: "Basic EPS", sectionStart: true },
      { key: "eps_diluted", label: "Diluted EPS" },
    ],
  },
  balance: {
    title: "Balance Sheet",
    primaryMetric: "total_assets",
    rows: [
      { key: "section_assets", label: "Assets", isGroupHeader: true, sectionStart: true },
      { key: "current_assets", label: "Current Assets", children: [
        { key: "cash", label: "Cash & Cash Equivalents" },
        { key: "short_term_investments", label: "Short-term Investments" },
        { key: "accounts_receivable", label: "Trade & Other Receivables" },
        { key: "inventory", label: "Inventories" },
        { key: "other_current_assets", label: "Other Current Assets" },
      ]},
      { key: "noncurrent_assets", label: "Non-current Assets", children: [
        { key: "property_plant_equipment", label: "Property, Plant & Equipment" },
        { key: "operating_lease_assets", label: "Right-of-Use Assets" },
        { key: "goodwill", label: "Goodwill" },
        { key: "intangible_assets", label: "Intangible Assets" },
        { key: "long_term_investments", label: "Non-current Financial Assets" },
        { key: "other_noncurrent_assets", label: "Other Non-current Assets" },
      ]},
      { key: "total_assets", label: "Total Assets" },
      { key: "section_liabilities", label: "Liabilities", isGroupHeader: true, sectionStart: true },
      { key: "current_liabilities", label: "Current Liabilities", children: [
        { key: "accounts_payable", label: "Trade & Other Payables" },
        { key: "current_debt", label: "Current Loans & Borrowings" },
        { key: "current_lease_liabilities", label: "Current Lease Liabilities" },
        { key: "provisions_current", label: "Current Provisions" },
        { key: "current_deferred_revenue", label: "Deferred Income (Current)" },
        { key: "other_current_liabilities", label: "Other Current Liabilities" },
      ]},
      { key: "noncurrent_liabilities", label: "Non-current Liabilities", children: [
        { key: "long_term_debt", label: "Non-current Loans & Borrowings" },
        { key: "noncurrent_lease_liabilities", label: "Non-current Lease Liabilities" },
        { key: "pension_obligations", label: "Post-employment Benefits" },
        { key: "provisions_noncurrent", label: "Non-current Provisions" },
        { key: "noncurrent_deferred_revenue", label: "Deferred Income (Non-current)" },
        { key: "deferred_tax_liabilities", label: "Deferred Tax Liabilities" },
        { key: "other_noncurrent_liabilities", label: "Other Non-current Liabilities" },
      ]},
      { key: "total_liabilities", label: "Total Liabilities" },
      { key: "total_debt", label: "Total Debt" },
      { key: "section_equity", label: "Equity", isGroupHeader: true, sectionStart: true },
      { key: "stockholders_equity", label: "Equity Attributable to Owners", children: [
        { key: "share_capital", label: "Share Capital" },
        { key: "additional_paid_in_capital", label: "Share Premium" },
        { key: "retained_earnings", label: "Retained Earnings / Accumulated Profit" },
        { key: "aoci", label: "Other Reserves" },
        { key: "minority_interest", label: "Non-controlling Interests" },
      ]},
      { key: "liabilities_and_equity", label: "Total Equity & Liabilities", sectionStart: true },
      { key: "shares_outstanding", label: "Shares Outstanding" },
    ],
  },
  cashflow: US_GAAP_STATEMENT_DEFS.cashflow,
  ratios: US_GAAP_STATEMENT_DEFS.ratios,
  custom: US_GAAP_STATEMENT_DEFS.custom,
  supplemental: US_GAAP_STATEMENT_DEFS.supplemental,
};

/** Income statement metric keys (derived from STATEMENT_DEFS for backward compat) */
export const INCOME_KEYS = allRowKeys(STATEMENT_DEFS.income.rows);

/** Balance sheet metric keys */
export const BALANCE_KEYS = allRowKeys(STATEMENT_DEFS.balance.rows);

/** Cash flow statement metric keys */
export const CASH_KEYS = allRowKeys(STATEMENT_DEFS.cashflow.rows);
export const RATIOS_KEYS = allRowKeys(STATEMENT_DEFS.ratios.rows);

/** Top-level income statement metric keys (excludes children and group headers) */
export const INCOME_KEYS_TOP = topLevelRowKeys(STATEMENT_DEFS.income.rows);

/** Top-level balance sheet metric keys (excludes children and group headers) */
export const BALANCE_KEYS_TOP = topLevelRowKeys(STATEMENT_DEFS.balance.rows);

/** Top-level cash flow metric keys (excludes children and group headers) */
export const CASH_KEYS_TOP = topLevelRowKeys(STATEMENT_DEFS.cashflow.rows);
export const RATIOS_KEYS_TOP = topLevelRowKeys(STATEMENT_DEFS.ratios.rows);

/** Chart.js color palette */
export const COLORS = {
  primary: "#3b82f6",
  green: "#10b981",
  red: "#ef4444",
  purple: "#8b5cf6",
  yellow: "#fbbf24",
  cyan: "#06b6d4",
  ghost: "rgba(255,255,255,.06)",
  grid: "rgba(255,255,255,.06)",
} as const;

/** Multi-series color palette (10 colors for charts builder) */
export const CB_COLORS = [
  "#3b82f6",
  "#10b981",
  "#8b5cf6",
  "#fbbf24",
  "#06b6d4",
  "#ef4444",
  "#f97316",
  "#ec4899",
  "#14b8a6",
  "#a3e635",
] as const;

/** Layer metrics for chart overlay (all available) */
export const LAYERABLE_METRICS = ALL_METRICS;

/* ═══════════════════════════════════════════════════════════════════════
   Color-family system  –  10 Tailwind hue families × 6 shades (300-800)
   ═══════════════════════════════════════════════════════════════════════ */

export const SHADE_LEVELS = [300, 400, 500, 600, 700, 800] as const;
export type ShadeLevel = (typeof SHADE_LEVELS)[number];

/** Tailwind CSS color values for each family/shade pair */
export const COLOR_FAMILIES: Record<string, Record<ShadeLevel, string>> = {
  blue: { 300: "#bfdbfe", 400: "#60a5fa", 500: "#3b82f6", 600: "#2563eb", 700: "#1e40af", 800: "#172554" },
  emerald: { 300: "#a7f3d0", 400: "#34d399", 500: "#10b981", 600: "#059669", 700: "#065f46", 800: "#022c22" },
  violet: { 300: "#ddd6fe", 400: "#a78bfa", 500: "#8b5cf6", 600: "#7c3aed", 700: "#5b21b6", 800: "#2e1065" },
  amber: { 300: "#fde68a", 400: "#fbbf24", 500: "#f59e0b", 600: "#d97706", 700: "#92400e", 800: "#451a03" },
  cyan: { 300: "#a5f3fc", 400: "#22d3ee", 500: "#06b6d4", 600: "#0891b2", 700: "#155e75", 800: "#083344" },
  red: { 300: "#fecaca", 400: "#f87171", 500: "#ef4444", 600: "#dc2626", 700: "#991b1b", 800: "#450a0a" },
  orange: { 300: "#fed7aa", 400: "#fb923c", 500: "#f97316", 600: "#ea580c", 700: "#9a3412", 800: "#431407" },
  pink: { 300: "#fbcfe8", 400: "#f472b6", 500: "#ec4899", 600: "#db2777", 700: "#9d174d", 800: "#500724" },
  teal: { 300: "#99f6e4", 400: "#2dd4bf", 500: "#14b8a6", 600: "#0d9488", 700: "#115e59", 800: "#042f2e" },
  lime: { 300: "#d9f99d", 400: "#a3e635", 500: "#84cc16", 600: "#65a30d", 700: "#3f6212", 800: "#1a2e05" },
} as const;

/** Ordered list of family names for auto-assignment */
export const FAMILY_NAMES = Object.keys(COLOR_FAMILIES);

/** Default shade level */
export const DEFAULT_SHADE: ShadeLevel = 600;

export type AccountingStandard = "us-gaap" | "ifrs-full";

export const TEMPLATES: Record<AccountingStandard, Record<StatementType, { title: string; primaryMetric: string; rows: StatementRow[] }>> = {
  "us-gaap": US_GAAP_STATEMENT_DEFS,
  "ifrs-full": IFRS_STATEMENT_DEFS,
};

export function getTopLevelStatementKeys(standard: AccountingStandard, type: StatementType) {
  return topLevelRowKeys(TEMPLATES[standard][type].rows);
}
